import { Container, SxProps, Typography, Box, Grid, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { ReactComponent as ArrowDownIcon } from '@shared/assets/arrow-down.svg'
import { ReactComponent as ArrowUpIcon } from '@shared/assets/arrow-up.svg'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

interface IProps {
  sx?: SxProps
}

interface FundData {
  currentPrice: string
  currency: string
  date: string
  description: string
  increase?: string
  decrease?: string
  chartData: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      borderColor: string
      backgroundColor: string
      tension: number
    }[]
  }
}

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}))

const MainTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h2,
  marginBottom: theme.spacing(5),
}))

const StyledCard = styled('div')(({ theme }) => ({
  backgroundColor: '#F3F5F9 ',
  margin: theme.spacing(0, -3),
  padding: theme.spacing(1.5, 3),

  [theme.breakpoints.up('md')]: {
    borderRadius: theme.spacing(2.5),
    padding: theme.spacing(3),
    margin: theme.spacing(0, 0),
  },
}))

const PriceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column-reverse',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}))

const PriceWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  width: 'fit-content',
  height: 'fit-content',
}))

const PriceStack = styled(Stack)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const PriceText = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  textWrap: 'nowrap',
}))

const DateText = styled(Typography)(({ theme }) => ({
  ...theme.typography.text14Regular,
  textWrap: 'nowrap',
  color: '#5D676B',
}))

const DescriptionText = styled(Typography)(({ theme }) => ({
  maxWidth: 310,
  ...theme.typography.text14Regular,
  lineHeight: '24px',
}))

const ChartContainer = styled(Box)({
  flex: 1,
  height: 200,
  position: 'relative',
})

// Mock data for demonstration
const getFundDataEUR = (t: (key: string) => string): FundData => ({
  currentPrice: '0,9563 EUR',
  currency: t('widgets:main-page.history-chart.fund-data.eur.currency'),
  date: '31.12.2025',
  description: t('widgets:main-page.history-chart.fund-data.eur.description'),
  increase: '+0.18%',
  chartData: {
    labels: [
      '31.12.2024',
      '31.03.2025',
      '30.06.2025',
      '30.09.2025',
      '31.12.2025',
    ],
    datasets: [
      {
        label: 'EUR Price',
        data: [0.05, 1.0, 1.062, 0.9546, 0.9563],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        tension: 0,
      },
    ],
  },
})

const getFundDataCZK = (t: (key: string) => string): FundData => ({
  currentPrice: '0,6897 CZK',
  currency: t('widgets:main-page.history-chart.fund-data.czk.currency'),
  date: '31.12.2025',
  description: t('widgets:main-page.history-chart.fund-data.czk.description'),
  decrease: '-0.96%',
  chartData: {
    labels: [
      '31.12.2024',
      '31.03.2025',
      '30.06.2025',
      '30.09.2025',
      '31.12.2025',
    ],
    datasets: [
      {
        label: 'CZK Price',
        data: [0.3, 1.0, 1.2235, 0.6964, 0.6897],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        tension: 0,
      },
    ],
  },
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
  scales: {
    x: {
      display: true,
      grid: {
        display: false,
      },
    },
    y: {
      display: true,
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.1)',
      },
      ticks: {
        callback: function (tickValue: string | number) {
          // Format values with commas and 4 decimal places
          const value =
            typeof tickValue === 'string' ? parseFloat(tickValue) : tickValue

          return value.toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          })
        },
        stepSize: 0.2, // Set step size to 0.2 for 0, 0.2, 0.4, 0.6, 0.8, 1.0
      },
    },
  },
  elements: {
    line: {
      tension: 0,
    },
    point: {
      radius: 4,
      hoverRadius: 6,
    },
  },
}

function FundCard({ data }: { data: FundData }) {
  const percentageChange = data.increase || data.decrease || '0.00%'
  const isIncrease = !!data.increase

  return (
    <StyledCard>
      <PriceContainer>
        <PriceWrapper>
          <PriceStack direction="row" alignItems="center" spacing={1}>
            <PriceText variant="h4">{data.currentPrice}</PriceText>

            <Stack direction="row" alignItems="center" spacing={'2px'}>
              {isIncrease && <ArrowUpIcon width={12} height={12} />}
              {!isIncrease && <ArrowDownIcon width={12} height={12} />}

              <Typography
                variant="text14Regular"
                color={isIncrease ? '#34C759' : '#F44336'}
              >
                {percentageChange}
              </Typography>
            </Stack>
          </PriceStack>

          <DateText variant="body2">{data.date}</DateText>
        </PriceWrapper>

        <DescriptionText variant="body2">{data.description}</DescriptionText>
      </PriceContainer>

      <ChartContainer>
        <Line data={data.chartData} options={chartOptions} />
      </ChartContainer>
    </StyledCard>
  )
}

export function HistoryChart({ sx }: IProps) {
  const { t } = useTranslation()

  const fundDataEUR = getFundDataEUR(t)
  const fundDataCZK = getFundDataCZK(t)

  return (
    <StyledContainer sx={sx} id="history-chart">
      {/* Main Title */}
      <MainTitle variant="h2">
        {t('widgets:main-page.history-chart.title')}
      </MainTitle>

      {/* Fund Cards Grid */}
      <Grid container spacing={7.5}>
        <Grid item xs={12} lg={6}>
          <FundCard data={fundDataEUR} />
        </Grid>

        <Grid item xs={12} lg={6}>
          <FundCard data={fundDataCZK} />
        </Grid>
      </Grid>
    </StyledContainer>
  )
}
